import { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, Cross1Icon } from '@radix-ui/react-icons';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.tsx';
import { TPlantWithAccessControl } from './user-plant-access-management.tsx';

const PLANTS_PER_PAGE = 5;

export const UserPlantAccessManagementMobile = ({
  plants,
  onMovePlant,
  isLoading,
  canAssign,
}: {
  plants: TPlantWithAccessControl[];
  onMovePlant: (plantId: string, targetColumnId: string) => void;
  isLoading: boolean;
  canAssign: boolean;
}) => {
  const availablePlants = plants
    ?.filter((p) => p.columnId === 'available-plants')
    ?.map((p) => ({
      value: p.plantId,
      label: p.basicDetails.name,
    }));
  const associatedPlants = plants
    .filter((p) => p.columnId === 'associated-plants')
    .sort((a, b) => {
      if (a.newlyAdded && !b.newlyAdded) return 1;
      if (!a.newlyAdded && b.newlyAdded) return -1;
      return 0;
    });

  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const numOfPages = associatedPlants?.length
    ? Math.trunc((associatedPlants.length - 1) / PLANTS_PER_PAGE) + 1
    : 1;
  const activePage = Math.min(currentPage, numOfPages);
  const indexOfLastPlant = activePage * PLANTS_PER_PAGE;
  const indexOfFirstPlant = indexOfLastPlant - PLANTS_PER_PAGE;
  const currentPlants = associatedPlants.slice(indexOfFirstPlant, indexOfLastPlant);

  if (isLoading)
    return (
      <div className="flex min-h-[332px] w-full flex-col">
        <Skeleton className="h-9" />

        <div className="mt-4 flex flex-col gap-2">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
        <div className="mt-4 flex h-full items-center justify-between">
          <Skeleton className="h-5 w-16" />

          <div className="flex gap-2">
            <Skeleton className="size-8" />
            <Skeleton className="size-8" />
          </div>
        </div>
      </div>
    );

  return (
    <div className="mt-4 flex min-h-[332px] flex-col justify-between">
      <div>
        <Popover open={comboboxOpen} onOpenChange={setComboboxOpen} modal>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={comboboxOpen}
              className="h-9 w-full justify-between font-normal"
              disabled={!canAssign || !availablePlants?.length}
            >
              {!canAssign
                ? 'Assignment disabled for this user'
                : availablePlants?.length
                  ? 'Assign a plant'
                  : 'All available plants are assigned'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search plants..." />
              <CommandList>
                <CommandEmpty>No plants found.</CommandEmpty>
                <CommandGroup>
                  {availablePlants?.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => {
                        onMovePlant(option.value, 'associated-plants');
                        setComboboxOpen(false);
                        const newNumOfPages =
                          Math.trunc(associatedPlants.length / PLANTS_PER_PAGE) + 1;
                        setCurrentPage(newNumOfPages);
                      }}
                    >
                      <span className="truncate">{option.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <div>
          {associatedPlants.length === 0 ? (
            <p className="text-muted-foreground mt-6 text-sm">No plants assigned</p>
          ) : (
            <ul className="mt-4 flex flex-col gap-2">
              {currentPlants.map((plant) => {
                return (
                  <li
                    key={plant.plantId}
                    className="flex items-center gap-2 overflow-hidden rounded-lg bg-slate-100 px-4 py-2 aria-disabled:opacity-50 aria-disabled:hover:ring-0"
                  >
                    <div className="flex min-w-0 flex-1 items-center">
                      <span className="truncate">{plant.basicDetails.name}</span>
                      {plant.newlyAdded && (
                        <span className="mt-1 ml-1 size-2 flex-none rounded-full bg-blue-400"></span>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        onMovePlant(plant.plantId, 'available-plants');

                        if (currentPlants.length % 5 === 1) {
                          setCurrentPage((currentPage) =>
                            currentPage === 1 ? currentPage : currentPage - 1
                          );
                        }
                      }}
                      className="hover:ring-primary shrink-0 p-1 hover:ring-2 hover:ring-inset"
                    >
                      <Cross1Icon />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center">
          <div className="flex items-center justify-center text-sm font-medium sm:w-[100px]">
            Page {activePage} of {numOfPages}
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (activePage <= 1) return;
                setCurrentPage((current) => current - 1);
              }}
              disabled={activePage <= 1}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (activePage >= numOfPages) return;
                setCurrentPage((current) => current + 1);
              }}
              disabled={activePage >= numOfPages}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
